<template lang="html">
<div class="watershed-details">
    <h3 class="headline watershed-name primary--text" v-text="name"></h3>

    <div class="loading" v-if="!queryDone">
        <p>Loading watershed data...</p>
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <div class="watershed" v-show="watershedId && queryDone">

        <p class="body-2 mb-1 network-info">
            WFI: {{ watershedId }}
        </p>

        <hr class="my-3">

        <div
        v-for="{ title, value } in properties"
        v-if="value"
        class="properties-item mb-3">
            <h4 class="subheading field-title grey--text" v-text="title"></h4>
            <div class="body-1 field-value" v-text="value"></div>
        </div>

        <hr class="my-3">

        <h4 class="subheading field-title grey--text">Watershed PDF Report</h4>
        <v-btn
            class="my-3"
            block
            large
            @click="generateReport"
            color="primary">
            <v-icon left>library_add</v-icon>
            Add to My Reports
        </v-btn>

        <v-btn
            class="my-3"
            color="info"
            block
            large
            @click="downloadGeoJSON">
            <v-icon left>cloud_download</v-icon>
            Download GeoJSON
        </v-btn>

        <v-btn block @click="clearSelection">
            <v-icon left>mdi-cancel</v-icon>
            Clear selection
        </v-btn>

    </div>

</div>
</template>

<script>
import axios from 'axios';
import gql from 'graphql-tag';
import numeral from 'numeral';
import fileDownload from 'js-file-download';

import { apolloClient } from '@/apollo/apollo';
import * as actions from '@/constants/actions';
import checkQueryData from '@/mixins/checkQueryData';
import watershedData from '@/mixins/watershedData';
import mapMixin from '@/mixins/map';

// components
import SparkLine from '@/components/SidebarSparkline';

export default {
    name: 'watershed-details',
    mixins: [
        checkQueryData,
        watershedData,
        mapMixin,
    ],
    components: {
        SparkLine,
    },
    data() {
        return {
            name: 'Watershed',
            fwa: null,
            queryDone: false,
            properties: [],
            outletCoords: null,
            queryCoords: null,
            meanElevation: null,
            upstreamArea: null,
            mad: null,
        };
    },
    computed: {
        selectedWatershed() {
            return this.$store.state.map.selectedWatershed;
        },
        watershedId() {
            if (this.selectedWatershed) {
                return this.selectedWatershed.id;
            }
            return null;
        },
    },
    mounted() {
        this.initialize();
    },
    methods: {
        /**
         * handle initial data fetching and potentially watershed changes
         */
        async initialize() {
            // reset local vars
            this.queryDone = false;
            this.queryData = {};

            // fetch data
            await this.getData();
            this.buildProps();
        },
        /**
         * retrieve and download watershed GeoJSON
         */
        async downloadGeoJSON() {
            const { data } = await axios.get(`${this.$cfg.API_URL}/watershed/${this.watershedId}.geojson`, {
                responseType: 'blob',
            });
            fileDownload(data, `watershed-${this.watershedId}.geojson`);
        },
        /**
         * query the API and fetch watershed data
         */
        async getData() {
            this.$store.dispatch(actions.ADD_LOADING, 'load watershed data');

            try {
                const wfi = this.watershedId;

                // query for watershed details
                const { data } = await apolloClient.query({
                    query: gql`query getWatershed($wfi: Int!) {
                        getWatershedWFI(wfi: $wfi) {
                            wfi
                            fwa
                            name
                            rollup {
                                mean_annual_discharge
                                upstream_area
                            }
                            elevation {
                                max
                                mean
                                min
                            }
                            outlet_coordinates
                        }
                    }`,
                    variables: {
                        wfi,
                    },
                });

                const queryData = data.getWatershedWFI;

                // store results
                this.name = queryData.name;
                this.fwa = queryData.fwa;
                this.mad = queryData.rollup.mean_annual_discharge;
                this.meanElevation = queryData.elevation.mean;
                this.minElevation = queryData.elevation.min;
                this.maxElevation = queryData.elevation.max;
                // Convert into KM2
                this.upstreamArea = queryData.rollup.upstream_area / 1000000;
                const outletCoordsXY = queryData.outlet_coordinates;
                this.outletCoords = {
                    lat: outletCoordsXY.y,
                    lng: outletCoordsXY.x,
                };
                if (this.selectedWatershed.coords) {
                    const [lng, lat] = this.selectedWatershed.coords;
                    this.queryCoords = {
                        lng,
                        lat,
                    };
                } else {
                    this.queryCoords = Object.assign(this.outletCoords);
                }

                this.queryDone = true;
            } catch (ex) {
                this.$store.dispatch(actions.TRIGGER_TOAST, 'Error retrieving watershed data');
            } finally {
                this.$store.dispatch(actions.REMOVE_LOADING, 'load watershed data');
            }
        },
        /**
         * build an array of properties to display
         */
        buildProps() {
            const properties = [];
            const numFormat = this.$options.filters.numFormat;

            if (this.queryCoords) {
                const { lat, lng } = this.queryCoords;
                properties.push({
                    title: 'Query Location',
                    value: `${numeral(lat).format('0,0.[00000]')}, ${numeral(lng).format('0,0.[00000]')}`,
                });
            }

            if (this.outletCoords) {
                const { lat, lng } = this.outletCoords;
                properties.push({
                    title: 'Outlet',
                    value: `${lat}, ${lng}`,
                });
            }

            if (this.upstreamArea) {
                const area = numFormat(this.upstreamArea, '0,0.[0]');
                properties.push({
                    title: 'Watershed Area',
                    value: `${area} km²`,
                });
            }
            // min, mean, max elevation (where available)
            const elevations = [];
            if (this.minElevation !== null) {
                const min = numFormat(this.minElevation, '0,0.[0]');
                elevations.push(`${min} m (min)`);
            }
            if (this.meanElevation !== null) {
                const mean = numFormat(this.meanElevation, '0,0.[0]');
                elevations.push(`${mean} m (mean)`);
            }
            if (this.maxElevation !== null) {
                const max = numFormat(this.maxElevation, '0,0.[0]');
                elevations.push(`${max} m (max)`);
            }
            if (elevations.length > 0) {
                properties.push({
                    title: 'Elevation',
                    value: elevations.join(', '),
                });
            }

            if (this.mad) {
                const mad = numFormat(this.mad, '0,0.[00]');
                properties.push({
                    title: 'Mean Annual Discharge',
                    value: `${mad} m³/s`,
                });
            }

            this.properties = properties;
        },
        /**
         * Generate a report for a watershed
         */
        async generateReport() {
            window.gtag('event', 'Report', { action: 'generate', label: 'User initiated report generation' });
            const watershed = this.watershedId;
            this.$root.$emit('report-generating', {
                name: this.name,
                wfi: watershed,
            });
            this.$store.dispatch(actions.TRIGGER_TOAST, 'Report added to queue');
            try {
                const { lng, lat } = this.queryCoords;
                const { data } = await axios.post(`${this.$cfg.API_URL}/report/watershed`, {
                    wfi: watershed,
                    fwa: this.fwa,
                    lat: Number(lat),
                    lng: Number(lng),
                    name: this.name,
                }, { timeout: 5000000 });
                this.$root.$emit('report-generated', {
                    file: data.file,
                    wfi: watershed,
                });
            } catch (ex) {
                this.$store.dispatch(actions.TRIGGER_TOAST, 'Error generating report');
            }
        },
        /**
         * clear selected watershed
         */
        clearSelection() {
            this.$store.dispatch(actions.SELECT_WATERSHED, null);
        },
    },
    watch: {
        selectedWatershed(val) {
            if (val && val.id) {
                // re-initialize component when a new watershed is selected
                this.initialize();
            }
        },
    },
};
</script>

<style scoped lang="scss">
.watershed-details {
    .no-data {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 5rem;
    }

    h3.watershed-name {
        clear: both;
    }

    .properties-item {
        .field-title {
            font-weight: 500;
        }
    }

    .loading {
        height: 40vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/SidebarWatershed.vue